import { useCallback, useRef, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { GAME_PARAMS, isInsideCircle } from '@beat-games/game-config';
import type { ServerMessage, ClientPositionMessage } from '@beat-games/shared-types';
import { useGameStore } from '../stores/gameStore';
import { useAuthStore } from '../stores/authStore';
import { useCreditsStore } from '../stores/creditsStore';

const API_BASE = import.meta.env.VITE_API_URL || '';

export function useGameEngine() {
  const wsRef = useRef<WebSocket | null>(null);
  const positionIntervalRef = useRef<number | null>(null);
  const outsideTimerRef = useRef<number | null>(null);
  const isConnectingRef = useRef<boolean>(false);

  const { publicKey } = useWallet();
  const { sessionToken } = useAuthStore();

  const {
    phase,
    circle,
    cursorPosition,
    setConnectionStatus,
    setSessionId,
    setErrorMessage,
    setErrorCode,
    setPhase,
    setCountdown,
    setCircle,
    setElapsed,
    setScore,
    setSurvivalTimeMs,
    setPrecisionError,
    setScoreRank,
    setBestRank,
    setEndReason,
    setIsInsideCircle,
    setOutsideStartTime,
    reset: resetStore,
  } = useGameStore();

  // Check if cursor is inside circle and handle failure
  useEffect(() => {
    if (phase !== 'playing' || !circle || !cursorPosition) return;

    const inside = isInsideCircle(
      cursorPosition.x,
      cursorPosition.y,
      circle.x,
      circle.y,
      circle.radius
    );

    setIsInsideCircle(inside);

    if (!inside) {
      if (outsideTimerRef.current === null) {
        outsideTimerRef.current = Date.now();
        setOutsideStartTime(outsideTimerRef.current);
      }
    } else {
      outsideTimerRef.current = null;
      setOutsideStartTime(null);
    }
  }, [phase, circle, cursorPosition, setIsInsideCircle, setOutsideStartTime]);

  // Cleanup function
  const cleanup = useCallback(() => {
    isConnectingRef.current = false;
    if (positionIntervalRef.current) {
      clearInterval(positionIntervalRef.current);
      positionIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    outsideTimerRef.current = null;
  }, []);

  // Handle WebSocket messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
        return;
      }

      switch (message.type) {
        case 'countdown':
          setPhase('countdown');
          setCountdown(message.seconds);
          break;

        case 'start':
          setPhase('playing');
          setCountdown(null);
          setCircle({
            x: 400, // Canvas center
            y: 300,
            radius: GAME_PARAMS.startRadius,
          });
          break;

        case 'state':
          setCircle(message.circle);
          setElapsed(message.elapsed);
          break;

        case 'end':
          // Set all game end data before changing phase to prevent
          // brief render where phase is 'ended' but survivalTimeMs is still null
          setScore(message.score);
          setSurvivalTimeMs(message.survivalTimeMs ?? null);
          setPrecisionError(message.precisionError ?? null);
          setScoreRank(message.scoreRank ?? null);
          setBestRank(message.bestRank ?? null);
          setEndReason(message.reason);
          setPhase('ended');
          cleanup();
          break;

        case 'error':
          console.error('Server error:', message.message);
          setConnectionStatus('error');
          cleanup();
          break;

        case 'ping':
          // Respond to server ping
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'pong' }));
          }
          break;

        case 'pong':
          // Server responded to our ping - connection is alive
          break;
      }
    },
    [setPhase, setCountdown, setCircle, setElapsed, setScore, setSurvivalTimeMs, setPrecisionError, setScoreRank, setBestRank, setEndReason, setConnectionStatus, cleanup]
  );

  // Send position to server (always send to keep connection alive)
  const sendPosition = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;

    const { cursorPosition, circle } = useGameStore.getState();

    // Use cursor position, or circle center as fallback, or canvas center
    const x = cursorPosition?.x ?? circle?.x ?? 400;
    const y = cursorPosition?.y ?? circle?.y ?? 300;

    const message: ClientPositionMessage = {
      type: 'position',
      x,
      y,
      t: Date.now(),
    };

    try {
      wsRef.current.send(JSON.stringify(message));
    } catch (error) {
      console.error('Failed to send position:', error);
    }
  }, []);

  // Connect to game
  const connectToGame = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('Connection already in progress');
      return;
    }

    // Close any existing connection before creating a new one
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (positionIntervalRef.current) {
      clearInterval(positionIntervalRef.current);
      positionIntervalRef.current = null;
    }

    isConnectingRef.current = true;

    // Use connected wallet or generate a dev wallet for testing
    const walletAddress = publicKey?.toBase58() || 'dev_' + Math.random().toString(36).substring(7);

    setConnectionStatus('connecting');
    setErrorMessage(null);
    setErrorCode(null);

    try {
      // Request game session
      const response = await fetch(`${API_BASE}/api/game/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken && { Authorization: `Bearer ${sessionToken}` }),
        },
        body: JSON.stringify({
          wallet: walletAddress,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        const errorMsg = data.error?.message ?? 'Failed to request game session';
        const errorCode = data.error?.code;

        // Map error codes to our error types
        if (errorMsg.toLowerCase().includes('no credits') || errorCode === 'NO_CREDITS') {
          setErrorCode('NO_CREDITS');
        } else if (errorMsg.toLowerCase().includes('insufficient') || errorCode === 'INSUFFICIENT_BALANCE') {
          setErrorCode('INSUFFICIENT_BALANCE');
        } else if (
          errorCode === 'RATE_LIMITED' ||
          errorCode === 'COOLDOWN' ||
          errorCode === 'DAILY_LIMIT' ||
          errorMsg.toLowerCase().includes('wait')
        ) {
          setErrorCode('RATE_LIMITED');
        } else if (errorCode === 'NOT_QUALIFIED') {
          setErrorCode('NOT_QUALIFIED');
        } else if (errorCode === 'ELIMINATED') {
          setErrorCode('ELIMINATED');
        } else {
          setErrorCode('CONNECTION_FAILED');
        }

        // Store the actual error message for display
        setErrorMessage(errorMsg);
        throw new Error(errorMsg);
      }

      const { sessionId, wsUrl } = data.data;
      setSessionId(sessionId);

      // Refresh credits immediately after successful game request (credit was consumed)
      if (publicKey) {
        useCreditsStore.getState().fetchCredits(publicKey.toBase58());
      }

      // Connect WebSocket with wallet address as query param
      // Use URL API to properly append query parameters
      const wsUrlObj = new URL(wsUrl, window.location.origin);
      wsUrlObj.searchParams.set('wallet', walletAddress);
      const ws = new WebSocket(wsUrlObj.toString());
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        setConnectionStatus('connected');
        // Send ready message
        ws.send(JSON.stringify({ type: 'ready' }));

        // Start sending positions
        positionIntervalRef.current = window.setInterval(
          sendPosition,
          GAME_PARAMS.positionSendRate
        );
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        isConnectingRef.current = false;
        setConnectionStatus('error');
      };

      ws.onclose = () => {
        isConnectingRef.current = false;
        setConnectionStatus('disconnected');
        // If game was in progress, treat unexpected close as a disconnection failure
        const currentPhase = useGameStore.getState().phase;
        if (currentPhase === 'playing' || currentPhase === 'countdown') {
          setPhase('ended');
          setEndReason('disconnected');
          setErrorMessage('Connection lost');
        }
        cleanup();
      };
    } catch (error) {
      console.error('Failed to connect:', error);
      isConnectingRef.current = false;
      setConnectionStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to connect');
    }
  }, [
    publicKey,
    sessionToken,
    setConnectionStatus,
    setSessionId,
    setErrorMessage,
    setErrorCode,
    setPhase,
    setEndReason,
    sendPosition,
    handleMessage,
    cleanup,
  ]);

  // Reset game state
  const reset = useCallback(() => {
    cleanup();
    resetStore();
  }, [cleanup, resetStore]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    connectToGame,
    reset,
  };
}
